import { ExternalLink, ImageOff } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Badge, CopyButton, Skeleton } from '../../components/ui'
import { MARKETPLACE_NAMES, type ProfileAttributes } from './profileModel'
import type { ProfileHeader } from './useProfileData'

interface ProfileHeaderBlockProps {
  article: string
  header: ProfileHeader
  attributes: ProfileAttributes
  attributesLoading: boolean
  /** Закрыть панель при переходе по ссылке. */
  onNavigate: () => void
}

const LINK = 'inline-flex items-center gap-1 text-indigo-600 hover:underline dark:text-indigo-400'

/**
 * Шапка профиля: фото (из аналитики продаж), артикул моноширинным, название (каталог товаров), кабинеты, категория,
 * тема и ссылки на товар и макет. Всё необязательное: не пришло — поля нет, профиль продолжает работать.
 */
export function ProfileHeaderBlock({ article, header, attributes, attributesLoading, onNavigate }: ProfileHeaderBlockProps) {
  const designCode = attributes.designCode ?? header.designCode
  const category = attributes.category ?? header.category
  return (
    <section aria-label="Артикул" className="flex gap-4">
      {header.imageUrl ? (
        <img src={header.imageUrl} alt="" className="h-24 w-24 shrink-0 rounded-lg border border-gray-200 object-cover dark:border-zinc-700" />
      ) : (
        <span className="flex h-24 w-24 shrink-0 items-center justify-center rounded-lg border border-dashed border-gray-200 text-gray-300 dark:border-zinc-700 dark:text-zinc-600">
          <ImageOff className="h-6 w-6" aria-hidden="true" />
        </span>
      )}
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-base font-semibold text-gray-900 dark:text-zinc-100">{article}</span>
          <CopyButton value={article} label="артикул" />
        </div>
        {header.title && <p className="text-sm text-gray-700 dark:text-zinc-300">{header.title}</p>}
        {attributesLoading && attributes.sellers.length === 0 ? (
          <Skeleton className="h-5 w-64" />
        ) : (
          <div className="flex flex-wrap items-center gap-1.5">
            {attributes.sellers.map((seller) => (
              <Badge key={`${seller.marketplace}:${seller.sellerId}`} variant={seller.marketplace === 'wb' ? 'purple' : 'blue'}>
                {MARKETPLACE_NAMES[seller.marketplace]} · {seller.label}
              </Badge>
            ))}
            {category && <Badge variant="gray">{category}</Badge>}
            {attributes.themeNumber && <Badge variant="gray">Тема {attributes.themeNumber}</Badge>}
          </div>
        )}
        <div className="flex flex-wrap gap-4 text-xs">
          <Link to={`/products?article=${encodeURIComponent(article)}`} className={LINK} onClick={onNavigate}>
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
            Товар
          </Link>
          {designCode && (
            <Link to={`/designs?design=${encodeURIComponent(designCode)}`} className={LINK} onClick={onNavigate}>
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
              Макет <span className="font-mono">{designCode}</span>
            </Link>
          )}
        </div>
      </div>
    </section>
  )
}
